import { useEffect, useRef, useState } from 'react';
import { useNotifications } from './NotificationProvider';
import { timeAgoFa, toFaDigits, type NotificationItem } from './types';
import { useAuth } from '../auth/AuthProvider';
import { resolveNotificationTarget, type NotificationTarget } from './notificationRouting';

/**
 * The notification bell — an inline button (bell + unread badge) with a dropdown of recent
 * notifications, placed inside the nav chrome (desktop sidebar footer, mobile top bar). It does NOT
 * float: folding it into the nav removed a bottom-corner collision with the SmartEntryDock FAB and
 * declutters the screen. The messaging drawer itself is mounted once, globally, by MessagingMount —
 * so the two bell instances (desktop + mobile) can never double-render it.
 *
 * `align` picks the dropdown direction: 'up' from a bottom-anchored sidebar footer, 'down' from a top bar.
 * `onNavigate` routes a BUSINESS notification (deal/release/cheque/prices/accounting/assumptions) to its
 * record (E4-01) — supplied by AppShell via the sidebar; absent ⇒ those clicks just mark-read (as before).
 */
export function NotificationBell({
  align = 'down',
  onNavigate,
}: {
  align?: 'up' | 'down';
  onNavigate?: ((target: NotificationTarget) => void) | undefined;
}) {
  const { items, unread, markRead, openMessaging } = useNotifications();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on outside-click / Esc.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function onItem(n: NotificationItem) {
    if (!n.read) void markRead([n.id]);
    if (n.ref_type === 'thread' && n.ref_id) {
      openMessaging({ kind: 'user', userId: Number(n.ref_id), name: n.title });
      setOpen(false);
    } else if (n.ref_type === 'broadcast') {
      openMessaging({ kind: 'broadcast' });
      setOpen(false);
    } else if (user && onNavigate) {
      // Business notification → its record (E4-01). Degrades to mark-read only (null) for an unknown type,
      // a missing id, or a screen this role can't open — no dead click, no navigation to a forbidden screen.
      const target = resolveNotificationTarget(n.ref_type, n.ref_id, user.role);
      if (target) {
        onNavigate(target);
        setOpen(false);
      }
    }
  }

  return (
    <div className="nav-bell" ref={wrapRef}>
      <button
        type="button"
        className="nav-bell-btn"
        aria-label={unread > 0 ? `اعلان‌ها (${unread} خوانده‌نشده)` : 'اعلان‌ها'}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span aria-hidden>🔔</span>
        {unread > 0 ? (
          <span className="notif-badge">{unread > 99 ? '۹۹+' : toFaDigits(unread)}</span>
        ) : null}
      </button>

      {open ? (
        <div
          className={align === 'up' ? 'notif-pop pop-up' : 'notif-pop pop-down'}
          role="menu"
          aria-label="اعلان‌ها"
        >
          <div className="notif-pop-head">
            <b>اعلان‌ها</b>
            {unread > 0 ? (
              <button type="button" className="linklike" onClick={() => void markRead()}>
                خواندهٔ همه
              </button>
            ) : null}
          </div>
          <div className="notif-scroll">
            {items.length === 0 ? (
              <p className="muted" style={{ padding: '18px 14px', margin: 0, textAlign: 'center' }}>
                اعلانی نیست.
              </p>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  className={n.read ? 'notif-item' : 'notif-item notif-unreadrow'}
                  onClick={() => onItem(n)}
                >
                  {!n.read ? <span className="notif-dot" aria-hidden /> : null}
                  <span className="notif-body">
                    <span className="notif-title">{n.title}</span>
                    {n.body ? <span className="notif-sub">{n.body}</span> : null}
                    <span className="notif-time">{timeAgoFa(n.created_at)}</span>
                  </span>
                </button>
              ))
            )}
          </div>
          <button
            type="button"
            className="notif-openmsg"
            onClick={() => {
              openMessaging();
              setOpen(false);
            }}
          >
            ✉️ باز کردن پیام‌ها
          </button>
        </div>
      ) : null}
    </div>
  );
}
